import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import Api from 'fou-api';
import i18n from 'src/assets/i18n/i18n';
import { Toaster } from 'src/utils/helper/toaster';
import Errors from 'src/utils/helper/errors';
import { UPLOAD_FLIP_FONTS, DROPSHIP } from 'src/utils/constants/app-routes-name';

const { t } = i18n().global;

export function useDropship() {
    // Router
    const router = useRouter();

    // Store
    const store = useStore();

    // Computed properties for dropship state, based on Vuex
    const isNewDropShip = computed({
        get: () => store.getters['dropship/isNewDropShipCreated'],
        set: value => {
            store.commit('dropship/setIsNewDropShipCreated', value);
        }
    });
    const dropShipData = computed({
        get: () => store.getters['dropship/getDropShipData'],
        set: value => {
            store.commit('dropship/setDropShipData', value);
        }
    });
    const isDropShipFormInError = computed({
        get: () => store.getters['dropship/isDropShipFormInError'],
        set: value => {
            store.commit('dropship/setDropShipFormInError', value);
        }
    });

    /**
     * Create a new dropship project
     * @param {Object} projectData - The project data
     * @param {string} projectData.projectName - Name of the project
     * @param {string} projectData.type - Type of dropship project
     * @param {string} projectData.foundryId - ID of the target foundry
     */
    const createDropship = async projectData => {
        const result = {
            success: false,
            data: null,
            error: {
                status: '',
                message: ''
            }
        };
        try {
            // Validate required fields
            if (!projectData.projectName || !projectData.type || !projectData.foundryId) {
                return null;
            }

            // Dispatch the api
            const response = await Api.DropShip.createDropShip(projectData);
            result.success = true;
            result.data = response.data;
        } catch (error) {
            result.success = false;
            result.data = null;
            if (error?.response?.status === 409) {
                result.error.status = error?.response?.status;
                result.error.message = error?.response?.data?.message;
            }
        }

        return result;
    };

    /**
     * Navigate to dropship details page
     * @param {string} dropshipId - ID of the dropship to view
     */
    const navigateToDropship = (dropshipId, isNew = true) => {
        if (!dropshipId) {
            console.error('Dropship ID is required for navigation');
            return;
        }
        isNewDropShip.value = isNew;
        router.push({
            name: UPLOAD_FLIP_FONTS,
            params: { id: dropshipId }
        });
    };

    const getDropShipData = async obj => {
        const result = {
            success: false,
            data: null,
            error: {
                message: ''
            }
        };
        try {
            const response = await Api.DropShip.getDropShip(
                obj.dropShipType,
                obj.status,
                obj.page,
                obj.perPage,
                obj.sortBy,
                obj.sortDirection,
                obj.search
            );
            result.success = true;
            result.data = response.data;
        } catch (error) {
            const err = Errors.create(error);
            result.error = err;
        }
        return result;
    };

    const editDropship = async projectData => {
        const { dropShipId, ...rest } = projectData;
        const result = {
            success: false,
            data: null,
            error: {
                status: '',
                message: ''
            }
        };

        try {
            const response = await Api.DropShip.patchDropShip(dropShipId, rest);
            result.success = true;
            result.data = response.data;
            dropShipData.value = {
                ...dropShipData.value,
                metadata: response?.data?.data?.metadata
            }
        } catch (error) {
            result.success = false;
            result.data = null;
            if (error?.response?.status === 409) {
                result.error.status = error?.response?.status;
                result.error.message = error?.response?.data?.message;
            }
        }

        return result;
    };

    const getDropShipDataById = async dropShipId => {
        try {
            const response = await Api.DropShip.getDropShipById(dropShipId);
            if (response?.data?.success) {
                dropShipData.value = response.data.data;
            } else {
                throw new Error('Failed to get dropShip');
            }
        } catch (error) {
            console.error('Error fetching dropship data by ID:', error);
            Toaster.notifyError(t('pages.dropShip.flip_fonts_listing.flip_font_default_error'));
        }
    };

    const deleteDropShipFiles = async (dropShipId, dropShipFileIds) => {
        let status = false;
        try {
            await saveDropShipDraft(dropShipId, false)
            const response = await Api.DropShip.deleteDropShipFiles(dropShipId, { fileIds: dropShipFileIds });
            if (response?.data?.success) {
                await getDropShipDataById(dropShipId);
                status = true;
                Toaster.notifySuccess(
                    t('pages.dropShip.flip_fonts_listing.flip_font_file_deleted', { filesCount: dropShipFileIds.length })
                );
            } else {
                throw new Error('Failed to delete dropShipFiles');
            }
        } catch (error) {
            console.log('Error in deleteDropShipFiles:', error);
            Toaster.notifyError(t('pages.dropShip.flip_fonts_listing.flip_font_default_error'));
        }
        return status;
    };

    const deleteDropShip = async (dropShipId, dropShipName) => {
        let result = {
            success: false
        };

        try {
            const response = await Api.DropShip.deleteDropShip(dropShipId);
            if (response?.data?.success) {
                result.success = true;
                Toaster.notifySuccess(
                    t('pages.dropShip.dashboard.delete_confirmation_modal.delete_success', { name: dropShipName })
                );
            } else {
                throw new Error('Failed to delete dropShip');
            }
        } catch (error) {
            console.error('Error in deleteDropShip:', error);
            Toaster.notifyError(result.error);
        }

        return result;
    };

    const saveDropShipDraft = async (dropShipId, showToast = true) => {
        let result = {
            success: false,
            data: null,
            error: []
        };
        try {
            const response = await Api.DropShip.updateDropShipFiles(dropShipId, {
                files: dropShipData.value.files
            });
            if (response.data.success) {
                result.data = response.data.data;
                if (showToast) {
                    Toaster.notifySuccess(t('pages.dropShip.upload_flip_fonts.save_draft_success'));
                }
            }
            if (response.data.error) {
                result.error = response.data.error;
            }
            result.success = true;
        } catch (error) {
            console.error('saveDropShipDraft:', error);
            result.success = false;
            result.data = null;
        }
        return result;
    };

    const submitDropShip = async dropShipId => {
        let result = {
            success: false,
            data: null,
            error: []
        };
        try {
            const saveDraftResult = await saveDropShipDraft(dropShipId, false);
            if (saveDraftResult.error.length > 0) {
                result.error = saveDraftResult?.error || [];
                return result;
            }
            const response = await Api.DropShip.submitDropShip(dropShipId);
            if (!response.data.success) throw new Error('Failed to Submit draft');
            router.push({ name: DROPSHIP });
            result.success = true;
            result.data = response.data.data;
        } catch (error) {
            Toaster.notifyError(t('pages.dropShip.upload_flip_fonts.submit_failed'));
            console.error('submitDropShip:', error);
            result.error = error;
            result.success = false;
            result.data = null;
        }
        return result;
    };

    /**
     * The function `uploadDropShipFiles` uploads drop ship files and handles any errors that occur during
     * the process.
     * @param dropShipId - The `dropShipId` parameter is the unique identifier of the dropship that the
     * files will be uploaded to. It is used to associate the uploaded files with the specific dropship in
     * the system.
     * @param dropShipFiles - The `dropShipFiles` parameter in the `uploadDropShipFiles` function
     * represents the files that are being uploaded for a specific dropship. These files could be any type
     * of data or documents related to the dropship process, such as images, documents, or other media
     * files. The function is
     * @returns The `errors` variable is being returned from the `uploadDropShipFiles` function.
     */
    const uploadDropShipFiles = async (dropShipId, dropShipFiles) => {
        let errors = null;
        try {
            if (dropShipData.value?.files?.length > 0) {
                await saveDropShipDraft(dropShipId);
            }
            const response = await Api.DropShip.createDropShipFiles(dropShipId, { files: dropShipFiles });
            if (response?.data?.success) {
                errors = response.data.error || [];
                Toaster.notifySuccess(t('pages.dropShip.flip_fonts_listing.flip_font_file_uploaded'));
            } else {
                throw new Error('Failed to upload dropShipFiles');
            }
        } catch (error) {
            console.error('Error in uploadDropShipFiles:', error);
            Toaster.notifyError(t('pages.dropShip.flip_fonts_listing.flip_font_default_error'));
        }
        return errors;
    };

    return {
        isNewDropShip,
        dropShipData,
        isDropShipFormInError,
        createDropship,
        navigateToDropship,
        getDropShipData,
        editDropship,
        getDropShipDataById,
        deleteDropShipFiles,
        deleteDropShip,
        saveDropShipDraft,
        submitDropShip,
        uploadDropShipFiles
    };
}
